import { useEffect, useState, useCallback } from "react";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Sparkles,
  RotateCcw,
} from "lucide-react";

import {
  initializeYear,
  getMonth,
  getYearSummary,
  markDay,
  markDaysRange,
  resetDay,
} from "../api/academicCalendarApi";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Modal,
  Input,
  Alert,
  Skeleton,
  Checkbox,
} from "../components/ui";
import { useToast } from "../context/ToastContext";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_TYPE_STYLES = {
  WORKING: "bg-white dark:bg-neutral-900 hover:bg-neutral-50/80 text-neutral-800 dark:text-neutral-100 border-neutral-200 dark:border-neutral-800",
  HOLIDAY: "bg-danger-50/40 hover:bg-danger-50/80 text-danger-700 border-danger-200/50",
  HALF_DAY: "bg-amber-50/40 hover:bg-amber-50/80 text-amber-800 border-amber-200/50",
};

const DAY_TYPE_BADGE_VARIANTS = {
  WORKING: "success",
  HOLIDAY: "danger",
  HALF_DAY: "warning",
};

const AcademicCalendar = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [calendarData, setCalendarData] = useState(null);
  const [yearSummary, setYearSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const toast = useToast();

  // Mark single day modal
  const [markModal, setMarkModal] = useState(null);

  // Mark range modal
  const [rangeModal, setRangeModal] = useState(false);
  const [rangeForm, setRangeForm] = useState({
    startDate: "",
    endDate: "",
    dayType: "HOLIDAY",
    reason: "",
    notification: "",
    skipSundays: true,
  });

  // Init year modal
  const [initModal, setInitModal] = useState(false);
  const [initYear, setInitYear] = useState(today.getFullYear());

  const [actionLoading, setActionLoading] = useState(false);
  const loadCalendar = useCallback(async () => {
    try {
      setLoading(true);
      const [monthData, summaryData] = await Promise.all([
        getMonth(year, month),
        getYearSummary(year),
      ]);
      setCalendarData(monthData);
      setYearSummary(summaryData);
    } catch (err) {
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const showSuccess = (msg) => {
    toast.success(msg);
    
  };

  const showError = (msg) => {
    toast.error(msg);
    
  };

  const handleInitYear = async () => {
    try {
      setActionLoading(true);
      const res = await initializeYear(initYear);
      setInitModal(false);
      showSuccess(
        `Year ${initYear} initialized — ${res.workingDays} working days, ${res.totalHolidays} holidays`
      );
      loadCalendar();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to initialize year");
    } finally {
      setActionLoading(false);
    }
  };

  const openMarkModal = (day) => {
    setMarkModal({
      date: day.date,
      dayType: day.dayType,
      reason: day.reason || "",
      notification: day.notification || "",
    });
  };

  const handleMarkDay = async () => {
    try {
      setActionLoading(true);
      await markDay(markModal);
      setMarkModal(null);
      showSuccess("Day updated successfully");
      loadCalendar();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to update day");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetDay = async (date) => {
    try {
      setActionLoading(true);
      await resetDay(date);
      setMarkModal(null);
      showSuccess("Day reset to default");
      loadCalendar();
    } catch (err) {
      showError("Failed to reset day");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkRange = async () => {
    try {
      setActionLoading(true);
      const res = await markDaysRange(rangeForm);
      setRangeModal(false);
      setRangeForm({
        startDate: "",
        endDate: "",
        dayType: "HOLIDAY",
        reason: "",
        notification: "",
        skipSundays: true,
      });
      showSuccess(`${res.daysMarked} days marked as ${rangeForm.dayType}`);
      loadCalendar();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to mark range");
    } finally {
      setActionLoading(false);
    }
  };

  const prevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
  };

  const renderGrid = () => {
    if (!calendarData) return null;

    const { calendar } = calendarData;
    const firstDayOfWeek = calendar[0]?.dayOfWeek || 0;
    const blanks = Array(firstDayOfWeek).fill(null);

    const todayStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    return (
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div
            key={d}
            className="text-center text-[10px] sm:text-xs font-medium text-neutral-400 dark:text-neutral-500 py-1 uppercase tracking-wider"
          >
            {d}
          </div>
        ))}

        {blanks.map((_, i) => (
          <div key={`blank-${i}`} className="min-h-[56px] sm:min-h-[72px]" />
        ))}

        {calendar.map((day) => {
          const isToday = day.date === todayStr;

          return (
            <div
              key={day.date}
              onClick={() => openMarkModal(day)}
              className={`
                border rounded-lg p-1.5 sm:p-2 min-h-[58px] sm:min-h-[72px] cursor-pointer transition-all flex flex-col justify-between
                ${DAY_TYPE_STYLES[day.dayType]}
                ${isToday ? "ring-1.5 ring-primary-500 border-primary-500 font-bold" : ""}
              `}
            >
              <div className="flex justify-between items-start">
                <span
                  className={`text-xs sm:text-sm ${
                    isToday ? "text-primary-600" : "text-neutral-800 dark:text-neutral-100"
                  }`}
                >
                  {day.day}
                </span>
                {day.dayType !== "WORKING" && (
                  <Badge
                    variant={DAY_TYPE_BADGE_VARIANTS[day.dayType]}
                    className="text-[9px] px-1 py-0 font-medium"
                  >
                    {day.dayType === "HOLIDAY" ? "H" : "½"}
                  </Badge>
                )}
              </div>
              {day.reason && (
                <p className="text-[10px] mt-0.5 leading-tight line-clamp-1 text-neutral-500 dark:text-neutral-400">
                  {day.reason}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-5 p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <div className="flex items-center gap-2.5">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
              Academic Calendar
            </h1>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
              Schedule holidays, half-days, and academic workdays.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            leftIcon={<CalendarDays className="h-3.5 w-3.5 text-neutral-600 dark:text-neutral-300" />}
            onClick={() => setRangeModal(true)}
            className="flex-1 sm:flex-initial text-xs border-neutral-200 dark:border-neutral-800"
          >
            Mark Range
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            leftIcon={<Sparkles className="h-3.5 w-3.5" />}
            onClick={() => setInitModal(true)}
            className="flex-1 sm:flex-initial text-xs"
          >
            Initialize Year
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Main Calendar Grid */}
        <Card className="lg:col-span-3 border border-neutral-200 dark:border-neutral-800 shadow-none">
          <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 py-2.5">
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={prevMonth}
                leftIcon={<ChevronLeft className="h-4 w-4" />}
                className="h-8 px-2 text-neutral-600 dark:text-neutral-300"
              />

              <CardTitle className="text-sm sm:text-base font-medium text-neutral-900 dark:text-neutral-100">
                {MONTH_NAMES[month - 1]} {year}
              </CardTitle>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={nextMonth}
                rightIcon={<ChevronRight className="h-4 w-4" />}
                className="h-8 px-2 text-neutral-600 dark:text-neutral-300"
              />
            </div>
          </CardHeader>

          <CardContent className="p-3 sm:p-5 space-y-4">
            {/* Minimal Horizontal Metrics Bar */}
            {calendarData && (
              <div className="flex items-center gap-4 text-xs font-medium text-neutral-500 dark:text-neutral-400 border-b border-neutral-100 dark:border-neutral-800 pb-3">
                <span>
                  <strong className="text-neutral-900 dark:text-neutral-100">{calendarData.working}</strong> Working
                </span>
                <span>•</span>
                <span>
                  <strong className="text-danger-600">{calendarData.holidays}</strong> Holidays
                </span>
                {calendarData.halfDays > 0 && (
                  <>
                    <span>•</span>
                    <span>
                      <strong className="text-amber-600">{calendarData.halfDays}</strong> Half Days
                    </span>
                  </>
                )}
              </div>
            )}

            {loading ? (
              <div className="space-y-3 py-4">
                <Skeleton height={16} width="30%" />
                <Skeleton height={280} width="100%" />
              </div>
            ) : (
              renderGrid()
            )}

            {/* Minimal Legend */}
            <div className="flex items-center gap-4 pt-2 text-[11px] text-neutral-400 dark:text-neutral-500">
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700" /> Working
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-danger-400" /> Holiday
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Half Day
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Minimal Year Summary Sidebar */}
        <Card className="h-fit border border-neutral-200 dark:border-neutral-800 shadow-none">
          <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 py-2.5">
            <CardTitle className="text-xs font-semibold text-neutral-700 dark:text-neutral-200 uppercase tracking-wider">
              {year} Overview
            </CardTitle>
          </CardHeader>

          <CardContent className="p-3 space-y-3">
            {yearSummary && (
              <>
                <div className="grid grid-cols-2 gap-2 text-center py-1">
                  <div className="p-2 rounded-md bg-neutral-50 dark:bg-neutral-950 border border-neutral-100 dark:border-neutral-800">
                    <p className="text-[10px] text-neutral-400 dark:text-neutral-500 font-medium">Working</p>
                    <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                      {yearSummary.totalWorking}
                    </p>
                  </div>
                  <div className="p-2 rounded-md bg-neutral-50 dark:bg-neutral-950 border border-neutral-100 dark:border-neutral-800">
                    <p className="text-[10px] text-neutral-400 dark:text-neutral-500 font-medium">Holidays</p>
                    <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                      {yearSummary.totalHolidays}
                    </p>
                  </div>
                </div>

                <div className="space-y-0.5 max-h-72 overflow-y-auto pr-0.5 border-t border-neutral-100 dark:border-neutral-800 pt-2">
                  {yearSummary.months.map((m) => (
                    <div
                      key={m.month}
                      onClick={() => setMonth(m.month)}
                      className={`flex items-center justify-between p-1.5 rounded text-xs cursor-pointer transition-colors ${ m.month === month ? "bg-neutral-100 dark:bg-neutral-800 font-semibold text-neutral-900 dark:text-neutral-100" : "hover:bg-neutral-50 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400" }`}
                    >
                      <span>{m.monthName.slice(0, 3)}</span>
                      <div className="flex gap-2 text-[11px]">
                        <span className="text-neutral-700 dark:text-neutral-200">{m.working}W</span>
                        <span className="text-neutral-400 dark:text-neutral-500">{m.holidays}H</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* MARK DAY MODAL */}
      {markModal && (
        <Modal
          isOpen={!!markModal}
          onClose={() => setMarkModal(null)}
          title={new Date(markModal.date).toLocaleDateString("en-IN", {
            weekday: "long",
            day: "numeric",
            month: "long",
          })}
          className="w-full max-w-sm"
        >
          <div className="space-y-3.5">
            <div>
              <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                Day Type
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {["WORKING", "HOLIDAY", "HALF_DAY"].map((type) => (
                  <Button
                    key={type}
                    type="button"
                    variant={markModal.dayType === type ? "primary" : "outline"}
                    size="sm"
                    onClick={() => setMarkModal({ ...markModal, dayType: type })}
                    className="text-xs"
                  >
                    {type === "HALF_DAY"
                      ? "Half Day"
                      : type.charAt(0) + type.slice(1).toLowerCase()}
                  </Button>
                ))}
              </div>
            </div>

            <Input
              label="Reason"
              type="text"
              value={markModal.reason}
              onChange={(e) =>
                setMarkModal({ ...markModal, reason: e.target.value })
              }
              placeholder="e.g. Festival, Extra Class"
            />

            <Input
              label="Notice"
              type="text"
              value={markModal.notification}
              onChange={(e) =>
                setMarkModal({ ...markModal, notification: e.target.value })
              }
              placeholder="Notice shown in portal"
            />

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleResetDay(markModal.date)}
                isLoading={actionLoading}
                leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                className="flex-1 text-xs"
              >
                Reset
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleMarkDay}
                isLoading={actionLoading}
                className="flex-1 text-xs"
              >
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* MARK RANGE MODAL */}
      {rangeModal && (
        <Modal
          isOpen={rangeModal}
          onClose={() => setRangeModal(false)}
          title="Mark Date Range"
          className="w-full max-w-sm"
        >
          <div className="space-y-3.5">
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Start Date"
                type="date"
                value={rangeForm.startDate}
                onChange={(e) =>
                  setRangeForm({ ...rangeForm, startDate: e.target.value })
                }
              />
              <Input
                label="End Date"
                type="date"
                value={rangeForm.endDate}
                onChange={(e) =>
                  setRangeForm({ ...rangeForm, endDate: e.target.value })
                }
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
                Day Type
              </label>
              <div className="grid grid-cols-3 gap-1.5">
                {["WORKING", "HOLIDAY", "HALF_DAY"].map((type) => (
                  <Button
                    key={type}
                    type="button"
                    variant={rangeForm.dayType === type ? "primary" : "outline"}
                    size="sm"
                    onClick={() =>
                      setRangeForm({ ...rangeForm, dayType: type })
                    }
                    className="text-xs"
                  >
                    {type === "HALF_DAY"
                      ? "Half Day"
                      : type.charAt(0) + type.slice(1).toLowerCase()}
                  </Button>
                ))}
              </div>
            </div>

            <Input
              label="Reason"
              type="text"
              value={rangeForm.reason}
              onChange={(e) =>
                setRangeForm({ ...rangeForm, reason: e.target.value })
              }
              placeholder="e.g. Winter Break"
            />

            <Input
              label="Notice"
              type="text"
              value={rangeForm.notification}
              onChange={(e) =>
                setRangeForm({ ...rangeForm, notification: e.target.value })
              }
              placeholder="Portal banner note"
            />

            <label className="flex items-center gap-2 cursor-pointer pt-0.5">
              <Checkbox
                checked={rangeForm.skipSundays}
                onChange={(e) =>
                  setRangeForm({ ...rangeForm, skipSundays: e.target.checked })
                }
              />
              <span className="text-xs text-neutral-600 dark:text-neutral-300">
                Skip Sundays
              </span>
            </label>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setRangeModal(false)}
                className="flex-1 text-xs"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleMarkRange}
                isLoading={actionLoading}
                disabled={!rangeForm.startDate || !rangeForm.endDate}
                className="flex-1 text-xs"
              >
                Apply Range
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* INIT YEAR MODAL */}
      {initModal && (
        <Modal
          isOpen={initModal}
          onClose={() => setInitModal(false)}
          title="Initialize Year"
          className="w-full max-w-xs"
        >
          <div className="space-y-3">
            <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
              Generates Sundays & standard holidays for the selected academic year.
            </p>

            <Input
              label="Year"
              type="number"
              value={initYear}
              onChange={(e) => setInitYear(Number(e.target.value))}
              min="2000"
              max="2100"
            />

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setInitModal(false)}
                className="flex-1 text-xs"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleInitYear}
                isLoading={actionLoading}
                className="flex-1 text-xs"
              >
                Initialize
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AcademicCalendar;